/**
 * User seed data generator
 */

import { faker } from '@faker-js/faker'
import { User, UserRole, Timezone, UserStatus } from '@/types'

const timezones = [Timezone.UTC, Timezone.EST, Timezone.CST, Timezone.MST, Timezone.PST]
const statuses = [
  UserStatus.Online,
  UserStatus.OnlineReadyToChat,
  UserStatus.OnlineInDeepWork,
  UserStatus.Offline,
  UserStatus.AwayFromKeyboard,
]

/**
 * Generate a single user
 */
export function generateUser(role: UserRole = UserRole.Member): User {
  const firstName = faker.person.firstName()
  const lastName = faker.person.lastName()
  const email = faker.internet.email({ firstName, lastName }).toLowerCase()

  return {
    id: faker.string.uuid(),
    createdAt: faker.date.past({ years: 1 }),
    updatedAt: faker.date.recent({ days: 30 }),
    name: `${firstName} ${lastName}`,
    email,
    password: `hashed_${faker.internet.password()}`,
    role,
    location: `${faker.location.city()}, ${faker.location.state({ abbreviated: true })}`,
    timezone: faker.helpers.arrayElement(timezones),
    status: faker.helpers.arrayElement(statuses),
    avatar: '',
    workspaces: [],
    projects: [],
    tasks: [],
    messages: [],
    documents: [],
  }
}

/**
 * Create Keenan Payne - SuperAdmin
 */
export function createKeenanPayne(): User {
  return {
    id: 'keenan-payne',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    name: 'Keenan Payne',
    email: 'keenan@axioma.com',
    password: 'hashed_Demo123!',
    role: UserRole.SuperAdmin,
    location: 'Denver, CO, USA',
    timezone: Timezone.PST,
    status: UserStatus.OnlineReadyToChat,
    avatar: '/keenan.jpg',
    workspaces: [],
    projects: [],
    tasks: [],
    messages: [],
    documents: [],
  }
}

/**
 * Create Jaime Hernández - SuperAdmin
 */
export function createJaimeHernandez(): User {
  return {
    id: 'jaime-hernandez',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    name: 'Jaime Hernández',
    email: 'jaime@axioma.com',
    password: 'hashed_Demo123!',
    role: UserRole.SuperAdmin,
    location: 'Chihuahua, Mexico',
    timezone: Timezone.PST,
    status: UserStatus.OnlineReadyToChat,
    avatar: '/jaime.png',
    workspaces: [],
    projects: [],
    tasks: [],
    messages: [],
    documents: [],
  }
}

/**
 * Generate multiple users
 * Always includes Keenan and Jaime as SuperAdmins, plus the specified count of random users
 */
export function generateUsers(count: number = 20): User[] {
  const users: User[] = []

  // Create fixed SuperAdmin users
  users.push(createKeenanPayne())
  users.push(createJaimeHernandez())

  // Create 2-3 Admins from random users
  const adminCount = faker.number.int({ min: 2, max: 3 })
  for (let i = 0; i < adminCount; i++) {
    users.push(generateUser(UserRole.Admin))
  }

  // Create remaining random users as Members
  const memberCount = count - adminCount
  for (let i = 0; i < memberCount; i++) {
    users.push(generateUser(UserRole.Member))
  }

  return users
}
